/**
 * fillMeshGeometry — the ONE conversion from an earcut
 * {@link TessellationResult} to a `THREE.BufferGeometry`.
 *
 * Every solid-fill factory in the scene needs the identical step: earcut hands
 * back a FLAT `[x0, y0, x1, y1, …]` array plus a parallel per-vertex Z and a
 * triangle index list, and three.js wants one interleaved `[x, y, z, …]`
 * position attribute plus the index. `buildHatchFillMesh` and
 * `buildWipeoutFillMesh` had written that loop out twice; the wide-polyline
 * band fill would have been the third, so it is a function (CLAUDE.md Этап 1
 * `dup:check` — the DEFAULT for a semantic clone is to extract the shared
 * helper, not to baseline it).
 *
 * # `mapPoint` — why the conversion takes a frame map
 *
 * earcut is 2D. A HATCH and a WIPEOUT are tessellated in WCS XY directly, so
 * they pass no map. A wide polyline's BAND is not necessarily in that plane: a
 * heavy POLYLINE with a non-default group-210 extrusion has its band built in
 * its OWN OCS plane, whose WCS-XY shadow is squashed and — for an extrusion
 * like (0,1,0) — has exactly zero area, which earcut would triangulate into
 * nothing. That caller therefore tessellates in the band's own plane and passes
 * the plane's `toWorld` here, so the triangles land back in the scene's frame.
 * Absent map ⇒ the coordinates ARE world coordinates and are copied verbatim,
 * byte-identical to the two loops this replaces.
 */

import * as THREE from "three";

import type { TessellationResult } from "../hatch/earcutLoops";
import type { Vec3 } from "../types";

/**
 * Pack an earcut result into a fresh indexed `BufferGeometry` with its bounding
 * box computed, optionally mapping every vertex through `mapPoint` first.
 */
export function tessellationToBufferGeometry(
  tess: TessellationResult,
  mapPoint?: (p: Vec3) => Vec3,
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const vertexCount = tess.positionsXY.length / 2;
  const positions = new Float32Array(vertexCount * 3);
  for (let i = 0; i < vertexCount; i += 1) {
    const raw: Vec3 = {
      x: tess.positionsXY[i * 2] ?? 0,
      y: tess.positionsXY[i * 2 + 1] ?? 0,
      z: tess.positionsZ[i] ?? 0,
    };
    const p = mapPoint ? mapPoint(raw) : raw;
    positions[i * 3] = p.x;
    positions[i * 3 + 1] = p.y;
    positions[i * 3 + 2] = p.z;
  }
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(tess.indices, 1));
  geometry.computeBoundingBox();
  return geometry;
}
